// src/components/pages/backtest/hooks/useBacktestSetup.js

import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
// 💡 수정: 유틸리티 함수 임포트 (JSON 직접 로드 대신)
import { isValidTickerLocal, validateTickerWithAPI } from "../../../../utils/tickerValidator";

// [HELPER 1: 초기 상태 로드 및 URL/localStorage 파싱 로직]
const getInitialStateFromURL = (locationSearch) => {
    const params = new URLSearchParams(locationSearch);
    const urlTickers = params.get('tickers');
    const urlWeightsString = params.get('weights'); 
    const urlPeriod = params.get('period');
    const urlBenchmark = params.get('benchmark'); 
    const urlStart = params.get('start');
    const urlEnd = params.get('end');
    
    const today = new Date();
    const tenYearsAgo = new Date(today);
    tenYearsAgo.setFullYear(today.getFullYear() - 30, today.getMonth(), today.getDate());
    today.setHours(0, 0, 0, 0); 
    tenYearsAgo.setHours(0, 0, 0, 0); 

    const formatDate = (date) => date.toISOString().split('T')[0];
    const todayString = formatDate(today); 
    const tenYearsAgoString = formatDate(tenYearsAgo); 

    let initialRows = [];
    let isReady = false; 
    const isCustomPeriodInURL = urlStart && urlEnd;

    if (urlTickers && urlWeightsString) {
      try {
        const tickersArray = urlTickers.split(',');
        const weightsArray = urlWeightsString.split(',').map(w => parseFloat(w.trim()));

        initialRows = tickersArray.map((ticker, index) => ({
          ticker: ticker.toUpperCase(),
          weight: weightsArray[index] || 0 
        })).filter(r => r.weight > 0); 

        const totalWeight = initialRows.reduce((acc, row) => acc + Number(row.weight || 0), 0);
        if (Math.abs(totalWeight - 100) < 0.1) isReady = true; 
      } catch (e) { /* console.error("Failed to parse URL state:", e); */ }
    }

    if (initialRows.length === 0) {
      const storedTickers = JSON.parse(localStorage.getItem("myStocks")) || [];
      const storedWeights = JSON.parse(localStorage.getItem("optimizedPortfolioWeights")) || {};
      if (storedTickers.length > 0 && Object.keys(storedWeights).length > 0) {
        initialRows = storedTickers.map(ticker => ({
          ticker,
          weight: parseFloat(storedWeights[ticker] || 0)
        })).filter(r => r.weight > 0);
      }
    }

    let initialBenchmark = urlBenchmark ? urlBenchmark.toUpperCase() : "GSPC.INDX";
    let initialBenchmarkInputMode = 'ticker'; 
    let initialBenchmarkRows = [{ ticker: '', weight: '' }];
    
    if (urlBenchmark) {
      if (urlBenchmark.includes(':') && urlBenchmark.includes(',')) {
          try {
              initialBenchmarkInputMode = 'portfolio';
              initialBenchmarkRows = urlBenchmark.split(',')
                  .map(item => {
                      const [ticker, weightStr] = item.split(':');
                      const weight = parseFloat(weightStr.trim()) * 100; 
                      return {
                          ticker: ticker.toUpperCase().trim(),
                          weight: weight.toFixed(0).toString() 
                      };
                  })
                  .filter(r => r.ticker && parseFloat(r.weight) > 0);

              initialBenchmark = ""; 
              if (initialRows.length > 0) { 
                isReady = true; 
              }
          } catch (e) { 
              initialBenchmarkInputMode = 'ticker'; 
              initialBenchmark = urlBenchmark.toUpperCase();
          }
      } else {
          initialBenchmarkInputMode = 'ticker';
          initialBenchmark = urlBenchmark.toUpperCase();
      }
    }
    
    return {
      rows: initialRows.length > 0 ? initialRows : [{ ticker: "", weight: "" }],
      period: urlPeriod ? (isCustomPeriodInURL ? 10 : Number(urlPeriod)) : 10,
      benchmark: initialBenchmark, 
      isReady, 
      initialStartDate: urlStart || tenYearsAgoString, 
      initialEndDate: urlEnd || todayString,
      initialIsCustom: isCustomPeriodInURL,
      initialBenchmarkInputMode: initialBenchmarkInputMode,
      initialBenchmarkRows: initialBenchmarkRows,
      tenYearsAgo: tenYearsAgo,
      today: today
    };
};

// 💡 훅 정의
export function useBacktestSetup(locationSearch, navigate) {
    const { t } = useTranslation();
    const initialState = useMemo(() => getInitialStateFromURL(locationSearch), [locationSearch]);
    
    const [rows, setRows] = useState(initialState.rows); 
    const [isBacktestReady, setIsBacktestReady] = useState(initialState.isReady); 
    const [backtestPeriod, setBacktestPeriod] = useState(initialState.period); 
    const [benchmark, setBenchmark] = useState(initialState.benchmark); 
    const [benchmarkInputMode, setBenchmarkInputMode] = useState(initialState.initialBenchmarkInputMode); 
    const [benchmarkRows, setBenchmarkRows] = useState(
        initialState.initialBenchmarkRows.length > 0 ? initialState.initialBenchmarkRows : [{ ticker: '', weight: '' }]
    ); 
    
    const [isCustomPeriod, setIsCustomPeriod] = useState(initialState.initialIsCustom);
    const [startDate, setStartDate] = useState(initialState.initialStartDate);
    const [endDate, setEndDate] = useState(initialState.initialEndDate);
    const [isLoadingBenchmark, setIsLoadingBenchmark] = useState(false);
    const [benchmarkError, setBenchmarkError] = useState("");
    const [message, setMessage] = useState("");

    // 1️⃣ [추가] 리밸런싱 주기 상태 관리 (기본값: none)
    const [rebalancePeriod, setRebalancePeriod] = useState("none");
    
    const { stocksArray, validRows, manualWeights, totalWeight } = useMemo(() => {
        const validRows = rows.filter(row => row.ticker && Number(row.weight) > 0);
        const stocksArray = validRows.map(row => row.ticker);
        const manualWeights = validRows.reduce((acc, row) => {
            acc[row.ticker.toUpperCase()] = row.weight;
            return acc;
        }, {});
        const totalWeight = rows.reduce((acc, row) => acc + Number(row.weight || 0), 0);
        return { stocksArray, validRows, manualWeights, totalWeight };
    }, [rows]);
    
    const benchmarkWeightSum = useMemo(() => 
        benchmarkRows.reduce((acc, row) => acc + Number(row.weight || 0), 0)
    , [benchmarkRows]);

    const handleAddRow = useCallback(() => setRows(prevRows => [...prevRows, { ticker: "", weight: "" }]), []);
    const handleChange = useCallback((index, field, value) => 
        setRows(prevRows => prevRows.map((row, i) => i === index ? { ...row, [field]: value } : row))
    , []);
    const handleRemoveRow = useCallback((index) => setRows(prevRows => prevRows.filter((_, i) => i !== index)), []);

    const handlePeriodChange = useCallback((value) => {
        const isCustom = value === 'custom';
        setIsCustomPeriod(isCustom); 
        setBacktestPeriod(isCustom ? 10 : Number(value));
        if (!isCustom) { setStartDate(initialState.initialStartDate); setEndDate(initialState.initialEndDate); }
    }, [initialState.initialStartDate, initialState.initialEndDate]);
    
    const handleBenchmarkChange = useCallback((index, field, value) => {
        setBenchmarkRows(prevRows => prevRows.map((row, i) => i === index ? { ...row, [field]: value } : row));
    }, []);

    const handleAddBenchmarkRow = useCallback(() => setBenchmarkRows(prevRows => [...prevRows, { ticker: '', weight: '' }]), []);
    const handleRemoveBenchmarkRow = useCallback((index) => setBenchmarkRows(prevRows => prevRows.filter((_, i) => i !== index)), []);
    
    const handleReset = useCallback(() => {
        setRows([{ ticker: "", weight: "" }]);
        localStorage.removeItem("myStocks");
        localStorage.removeItem("optimizedPortfolioWeights");
        setMessage(t("backtest.resetMessage"));
        navigate(window.location.pathname, { replace: true });
    }, [t, navigate]);

    const handleLoadExamples = useCallback(() => {
        setRows([{ ticker: "AAPL.US", weight: "40" }, { ticker: "MSFT.US", weight: "30" }, { ticker: "GOOG.US", weight: "30" }]);
        toast.info(t('loadExampleDisclaimer'));
    }, []);

    const handleResetBenchmark = useCallback(() => {
        setBenchmarkRows([{ ticker: '', weight: '' }]);
        setBenchmarkError(""); 
        setBenchmarkInputMode('portfolio'); 
        setMessage(t("backtest.benchmarkResetMessage") || "벤치마크 포트폴리오가 초기화되었습니다.");
    }, [t]);

    // 💡 벤치마크 유효성 검사 (유틸리티 함수 활용)
    const validateBenchmark = useCallback(async (skipLoadingState = false) => {
        if (benchmarkInputMode === 'portfolio') {
            const validBenchmarkRows = benchmarkRows.filter(row => row.ticker && row.weight !== '' && parseFloat(row.weight) > 0);
            if (validBenchmarkRows.length === 0) {
                setBenchmarkError(t("backtest.enterOneTickerAndWeightForBenchmark"));
                return false;
            }
            const sum = validBenchmarkRows.reduce((acc, row) => acc + parseFloat(row.weight), 0);
            if (Math.abs(sum - 100) > 0.1) {
                setBenchmarkError(t("backtest.weightSumMustBe100", { totalWeight: sum.toFixed(2) }));
                return false;
            }
            setBenchmarkError("");
            return true;
        }

        // Single Ticker 모드
        if (!skipLoadingState) setIsLoadingBenchmark(true);
        setBenchmarkError("");

        let validBenchmark = benchmark.toUpperCase();
        
        // 1) 로컬 데이터셋 확인 (유틸리티 사용)
        if (!isValidTickerLocal(validBenchmark)) {
            // 2) 로컬에 없으면 서버 API 확인 (유틸리티 사용)
            const verifiedTicker = await validateTickerWithAPI(validBenchmark);
            
            if (verifiedTicker) {
                validBenchmark = verifiedTicker;
            } else {
                setBenchmarkError(t("backtest.notValidTicker"));
                if (!skipLoadingState) setIsLoadingBenchmark(false);
                return false;
            }
        }

        if (!skipLoadingState) setIsLoadingBenchmark(false);
        setBenchmark(validBenchmark);
        return true;
    }, [benchmark, benchmarkInputMode, benchmarkRows, t]); 
    
    useEffect(() => {
        setIsBacktestReady(false);
        setMessage("");
    }, [rows, backtestPeriod, benchmark, startDate, endDate, isCustomPeriod, benchmarkInputMode, benchmarkRows, rebalancePeriod]);

    useEffect(() => {
      if (!initialState.isReady) return;

      const stocksArr = initialState.rows.map(row => row.ticker);
      const initialManualWeights = initialState.rows.reduce((acc, row) => {
          if (row.ticker && row.weight) acc[row.ticker.toUpperCase()] = String(row.weight);
          return acc;
      }, {});
      
      localStorage.setItem("myStocks", JSON.stringify(stocksArr));
      localStorage.setItem("optimizedPortfolioWeights", JSON.stringify(initialManualWeights));

      const checkAndRun = async () => {
          const isValid = await validateBenchmark(true); 
          if (isValid) setIsBacktestReady(true);
          else setMessage(t("backtest.benchmarkInvalidOnLoad") || "Loaded benchmark is invalid."); 
      };
      checkAndRun();
    }, [initialState.isReady, validateBenchmark, t]); 

    return {
        rows, setRows, isBacktestReady, setIsBacktestReady, backtestPeriod, setBacktestPeriod,
        benchmark, setBenchmark, benchmarkInputMode, setBenchmarkInputMode, benchmarkRows, setBenchmarkRows,
        isCustomPeriod, setIsCustomPeriod, startDate, setStartDate, endDate, setEndDate,
        isLoadingBenchmark, setIsLoadingBenchmark, benchmarkError, setBenchmarkError, message, setMessage,
        stocksArray, validRows, manualWeights, totalWeight, benchmarkWeightSum,
        tenYearsAgo: initialState.tenYearsAgo, today: initialState.today,
        rebalancePeriod,        // 2️⃣ [추가] 외부로 내보내기
        setRebalancePeriod,     // 3️⃣ [추가] 외부로 내보내기
        handleAddRow, handleChange, handleRemoveRow, handlePeriodChange, 
        handleBenchmarkChange, handleAddBenchmarkRow, handleRemoveBenchmarkRow, 
        handleReset, handleLoadExamples, handleResetBenchmark,
        validateBenchmark 
    };
}